import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Globe } from "lucide-react";
import { crmApi } from "../../lib/crmApi";
import { PageHead, BarList } from "./crmUi";

const KPIS = [
  ["new_leads_today", "New leads today", "/admin/crm/leads"],
  ["hot_leads", "Hot leads", "/admin/crm/leads?score=hot"],
  ["unassigned_leads", "Unassigned", "/admin/crm/leads?unassigned=1"],
  ["followups_due_today", "Follow-ups due today", "/admin/crm/tasks?due=today"],
  ["overdue_followups", "Overdue follow-ups", "/admin/crm/tasks?due=overdue"],
  ["site_visits_scheduled", "Site visits scheduled", "/admin/crm/leads?status=site_visit_scheduled"],
  ["quotations_pending", "Quotations pending", "/admin/crm/leads?status=quotation_pending"],
  ["won", "Won", "/admin/crm/leads?status=won"],
  ["lost", "Lost", "/admin/crm/leads?status=lost"],
];

export default function CrmDashboard() {
  const [d, setD] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    crmApi.dashboard().then(setD).catch((e) => setErr(e.message));
  }, []);

  return (
    <div data-testid="crm-dashboard">
      <PageHead eyebrow="Overview" title="Leads Dashboard">
        <Link
          to="/admin/crm/leads"
          data-testid="crm-dash-newlead-link"
          className="bg-[#6E1F2C] text-[#FAF5EC] px-4 py-2 text-[10px] tracking-[0.24em] uppercase hover:bg-[#56151F]"
        >
          + Manage Leads
        </Link>
      </PageHead>
      {err && <p className="text-xs text-[#A3141A]">{err}</p>}
      {!d && !err && <p className="text-xs text-[#7A6B5F]">Loading…</p>}
      {d && (
        <>
          {d.new_website_leads > 0 && (
            <Link
              to="/admin/crm/leads?source=website_form&status=new"
              data-testid="crm-website-leads-banner"
              className="flex items-center justify-between gap-3 bg-[#6E1F2C] text-[#FAF5EC] px-5 py-3 mb-5 hover:bg-[#56151F] transition-colors"
            >
              <span className="inline-flex items-center gap-2 text-[11px] tracking-[0.2em] uppercase">
                <Globe size={14} className="text-[#D4A963]" />
                {d.new_website_leads} new website {d.new_website_leads === 1 ? "lead" : "leads"} awaiting review
              </span>
              <span className="text-[9px] tracking-[0.24em] uppercase text-[#D4A963]">Review →</span>
            </Link>
          )}
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 mb-8">
            {KPIS.map(([k, l, to]) => (
              <Link
                key={k}
                to={to}
                data-testid={`crm-kpi-${k}`}
                className={`bg-white border p-4 hover:border-[#6E1F2C] transition-colors ${
                  (k === "overdue_followups" && d[k] > 0) || (k === "hot_leads" && d[k] > 0)
                    ? "border-[#A3141A]/40" : "border-[#6E1F2C]/10"
                }`}
              >
                <p className="font-serif-display text-3xl text-[#6E1F2C]">{d[k]}</p>
                <p className="text-[9px] tracking-[0.18em] uppercase text-[#7A6B5F] mt-1">{l}</p>
              </Link>
            ))}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-white border border-[#6E1F2C]/10 p-5">
              <p className="text-[10px] tracking-[0.26em] uppercase text-[#6E1F2C] mb-4">Leads by service category</p>
              <BarList items={d.by_category} testid="crm-bars-category" />
            </div>
            {d.by_salesperson?.length > 0 && (
              <div className="bg-white border border-[#6E1F2C]/10 p-5">
                <p className="text-[10px] tracking-[0.26em] uppercase text-[#6E1F2C] mb-4">Leads by salesperson</p>
                <BarList items={d.by_salesperson} testid="crm-bars-salesperson" />
              </div>
            )}
            <div className="bg-white border border-[#6E1F2C]/10 p-5">
              <p className="text-[10px] tracking-[0.26em] uppercase text-[#6E1F2C] mb-4">Lead source performance</p>
              <BarList items={d.by_source} testid="crm-bars-source" />
            </div>
            <div className="bg-white border border-[#6E1F2C]/10 p-5">
              <p className="text-[10px] tracking-[0.26em] uppercase text-[#6E1F2C] mb-4">Pipeline by status</p>
              <BarList items={d.by_status} testid="crm-bars-status" />
            </div>
          </div>
        </>
      )}
    </div>
  );
}
